import React, {useState, useEffect} from 'react';
import {
  Grid,
  Card,
  IconButton,
  Tooltip,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Avatar,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Delete,
  Edit,
  DragHandle,
  Visibility,
  VisibilityOff,
  Settings,
  Publish,
  Archive,
  Unpublished,
  Public,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {useParams, useNavigate} from 'react-router-dom';
import {Axios} from 'services/axios';
import {Block, Confirm, Notify} from 'notiflix';
import {Controller, useForm} from 'react-hook-form';
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';
import {useCountry} from 'context/CountryContext';
import 'react-quill/dist/quill.snow.css';

const STATUS_OPTIONS = [
  {value: 'draft', label: 'Draft', color: 'default'},
  {value: 'published', label: 'Published', color: 'success'},
  {value: 'archived', label: 'Archived', color: 'error'},
];

const PublishedSectionConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  section,
  action,
  loading,
}) => {
  const getActionDetails = () => {
    switch (action) {
      case 'delete':
        return {
          title: 'Delete Published Section',
          color: 'error',
          bgColor: '#f44336',
          icon: <Delete />,
          description:
            'This section is currently PUBLISHED and visible to users.',
          warnings: [
            '⚠️ Section will be permanently removed from the page',
            '⚠️ All content and settings will be lost forever',
            '⚠️ Users will immediately stop seeing this section',
            '⚠️ This might break the page layout',
            '⚠️ Consider archiving instead of deleting if you might need it later',
          ],
          confirmText: 'Yes, Delete Permanently',
        };
      case 'archive':
        return {
          title: 'Archive Published Section',
          color: 'warning',
          bgColor: '#ff9800',
          icon: <Archive />,
          description:
            'This section is currently PUBLISHED and visible to users.',
          warnings: [
            '⚠️ Section will be hidden from the live page immediately',
            '⚠️ Users will no longer see this content',
            '⚠️ You can restore it later from archive',
            '⚠️ Page layout will change',
            '⚠️ Make sure you have approval for this change',
          ],
          confirmText: 'Yes, Archive Section',
        };
      case 'draft':
        return {
          title: 'Change Published Section to Draft',
          color: 'warning',
          bgColor: '#ff9800',
          icon: <Unpublished />,
          description:
            'This section is currently PUBLISHED and visible to users.',
          warnings: [
            '⚠️ Section will be hidden from the live page immediately',
            '⚠️ All published content will be taken down',
            '⚠️ You can republish it later after reviewing',
            '⚠️ Page visitors will no longer see this content',
            '⚠️ This might affect ongoing campaigns or promotions',
          ],
          confirmText: 'Yes, Change to Draft',
        };
      default:
        return {
          title: 'Confirm Action',
          color: 'warning',
          bgColor: '#ff9800',
          icon: <Settings />,
          description: 'This section is currently PUBLISHED.',
          warnings: ['⚠️ This action will affect the live page'],
          confirmText: 'Yes, Proceed',
        };
    }
  };

  const details = getActionDetails();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}>
      <DialogTitle
        sx={{
          bgcolor: details.bgColor,
          color: 'white',
          py: 2,
        }}>
        <Box display="flex" alignItems="center" gap={1}>
          {details.icon}
          <MDTypography variant="h6" color="white" fontWeight="bold">
            {details.title}
          </MDTypography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{mt: 3, px: 3}}>
        {section && (
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              mb: 3,
              bgcolor: '#fafafa',
              borderLeft: `4px solid ${details.bgColor}`,
              borderRadius: 1,
            }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDTypography
                  variant="subtitle2"
                  color="text"
                  gutterBottom
                  sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <Public fontSize="small" color="action" />
                  Section Details:
                </MDTypography>
              </Grid>
              <Grid item xs={6}>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{fontWeight: 500}}>
                  Section Name:
                </MDTypography>
                <MDTypography variant="body2" fontWeight="bold">
                  {section.template?.name}
                </MDTypography>
              </Grid>
              <Grid item xs={6}>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{fontWeight: 500}}>
                  Current Status:
                </MDTypography>
                <Box sx={{mt: 0.5}}>
                  <Chip
                    label="PUBLISHED - LIVE"
                    color="success"
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: '#4caf50',
                      color: 'white',
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{fontWeight: 500}}>
                  Visibility:
                </MDTypography>
                <MDTypography variant="body2">
                  {section.isVisible ? 'Visible to all users' : 'Hidden'}
                </MDTypography>
              </Grid>
              <Grid item xs={6}>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{fontWeight: 500}}>
                  Position on Page:
                </MDTypography>
                <MDTypography variant="body2">
                  Section #{section.sectionOrder}
                </MDTypography>
              </Grid>
              {section.country && (
                <Grid item xs={12}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{fontWeight: 500}}>
                    Country:
                  </MDTypography>
                  <MDTypography variant="body2">
                    {section.country.countryName} ({section.country.countryCode}
                    )
                  </MDTypography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        <Box sx={{mb: 3}}>
          <MDTypography
            variant="subtitle2"
            sx={{
              color: details.color === 'error' ? '#d32f2f' : '#ed6c02',
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
            <span style={{fontSize: '1.2rem'}}>⚠️</span>
            Important Warnings - Please Read Carefully:
          </MDTypography>

          <Box
            component="ul"
            sx={{
              pl: 2,
              m: 0,
              bgcolor: '#fff4e5',
              p: 2,
              borderRadius: 1,
              border: '1px solid #ffe0b2',
            }}>
            {details.warnings.map((warning, index) => (
              <li key={index} style={{marginBottom: '8px'}}>
                <MDTypography
                  variant="body2"
                  color="error"
                  sx={{fontWeight: 500}}>
                  {warning}
                </MDTypography>
              </li>
            ))}
          </Box>
        </Box>

        <Alert severity="info" sx={{mt: 2, bgcolor: '#e3f2fd'}}>
          <MDTypography variant="body2" fontWeight="bold" gutterBottom>
            💡 Recommendation:
          </MDTypography>
          <MDTypography variant="body2">
            {action === 'delete'
              ? 'Instead of deleting, consider archiving the section. Archived sections can be restored later if needed. Only delete if you are absolutely sure you will never need this section again.'
              : 'Before changing the status of a published section, ensure you have: 1) Informed the content team, 2) Checked for any ongoing campaigns, 3) Got necessary approvals'}
          </MDTypography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{p: 3, pt: 2, gap: 1}}>
        <MDButton
          onClick={onClose}
          disabled={loading}
          sx={{minWidth: 100}}
          variant="outlined"
          color="secondary">
          Cancel
        </MDButton>
        <Button
          variant="contained"
          color={details.color}
          onClick={onConfirm}
          disabled={loading}
          startIcon={details.icon}
          sx={{
            minWidth: 180,
            bgcolor: details.bgColor,
            '&:hover': {
              bgcolor: details.color === 'error' ? '#d32f2f' : '#f57c00',
            },
            color: '#fff',
          }}>
          {details.confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Alert = ({severity, children, sx}) => {
  return (
    <Paper
      sx={{
        p: 2,
        bgcolor:
          severity === 'error'
            ? '#ffebee'
            : severity === 'warning'
            ? '#fff3e0'
            : severity === 'info'
            ? '#e3f2fd'
            : '#e8f5e8',
        borderLeft: `4px solid ${
          severity === 'error'
            ? '#f44336'
            : severity === 'warning'
            ? '#ff9800'
            : severity === 'info'
            ? '#2196f3'
            : '#4caf50'
        }`,
        ...sx,
      }}>
      <MDTypography variant="body2" color="text">
        {children}
      </MDTypography>
    </Paper>
  );
};

function PageSectionBuilder() {
  const {pageId} = useParams();
  const navigate = useNavigate();

  const {loading: countriesLoading, getCountryById} = useCountry();

  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addSectionDialog, setAddSectionDialog] = useState(false);
  const [editSectionDialog, setEditSectionDialog] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    section: null,
    action: null,
    pendingAction: null,
  });

  const [useDefaultContent, setUseDefaultContent] = useState(true);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    getValues,
    formState: {errors},
  } = useForm({
    defaultValues: {
      name: '',
      isActive: true,
      isVisible: true,
      status: 'draft',
      customCssClass: '',
      customId: '',
    },
  });

  useEffect(() => {
    fetchPageData();
  }, [pageId]);

  useEffect(() => {
    if (pageId) {
      fetchAvailableTemplates();
    }
  }, [pageId]);

  useEffect(() => {
    if (sections.length > 0 && availableTemplates.length > 0) {
      const usedTemplateIds = sections.map(section => section.templateId);

      const available = availableTemplates.filter(
        template => !usedTemplateIds.includes(template.id),
      );

      setFilteredTemplates(available);
    } else {
      setFilteredTemplates(availableTemplates);
    }
  }, [sections, availableTemplates]);

  useEffect(() => {
    if (loading || countriesLoading) {
      Block.circle('#page-sections', 'Loading...');
    } else {
      Block.remove('#page-sections');
    }
  }, [loading, countriesLoading]);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      const pageRes = await Axios.fetch(`page/${pageId}`);
      setPage(pageRes?.data?.data?.page);

      const sectionsRes = await Axios.fetch(`page/${pageId}/sections`);
      setSections(sectionsRes?.data?.data?.sections || []);
    } catch (error) {
      console.error('Error fetching page data:', error);
      Notify.failure('Error loading page sections', {
        timeout: 3000,
        position: 'center-top',
        cssAnimationStyle: 'from-top',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTemplates = async () => {
    try {
      // const res = await Axios.fetch(
      //   '/section-templates?isActive=true&limit=20',
      // );

      const res = await Axios.fetch(
        // Sorted by name — a low limit silently dropped every template past
        // the first 50 (e.g. "SEO FAQ (Hidden)") from the list
        `/section-templates/for-page/${pageId}?limit=1000`,
      );

      setAvailableTemplates(res.data.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      Notify.failure('Error loading templates', {
        timeout: 3000,
        position: 'center-top',
      });
    }
  };

  const fetchTemplateDetails = async templateId => {
    try {
      const res = await Axios.fetch(`section-templates/${templateId}`);
      const templateData = res.data.data.template;
      setTemplateDetails(templateData);
      return templateData;
    } catch (error) {
      console.error('Error fetching template details:', error);
      Notify.failure('Error loading template details', {timeout: 3000});
      return null;
    }
  };

  const getCountryDetails = countryId => {
    if (!countryId) return null;
    return getCountryById(countryId);
  };

  const handleDragEnd = async result => {
    if (!result.destination) return;

    const items = Array.from(sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSections(items);

    const orderData = items.map((item, index) => ({
      id: item.id,
      order: index + 1,
    }));

    try {
      await Axios.put(`page/${pageId}/sections/reorder`, {
        sectionOrders: orderData,
      });
      Notify.success('Section order updated successfully', {
        timeout: 2000,
        position: 'center-top',
      });
    } catch (error) {
      console.error('Error reordering sections:', error);
      Notify.failure('Error saving order', {
        timeout: 3000,
        position: 'center-top',
      });
      fetchPageData();
    }
  };

  const handleAddSection = async template => {
    setSelectedTemplate(template);
    setUseDefaultContent(true);

    await fetchTemplateDetails(template.id);

    reset({
      name: template.name,
      isActive: true,
      isVisible: true,
      status: 'draft',
      customCssClass: '',
      customId: '',
    });

    setAddSectionDialog(true);
  };

  const onSubmitAddSection = async data => {
    try {
      Block.circle('#add-section-dialog', 'Adding section...');

      console.log('data : ', data);

      const payload = {
        templateId: selectedTemplate.id,
        // slotId: selectedTemplate.slotId, 
        typeKey: selectedTemplate.typeKey, 
        sectionOrder: sections.length + 1,
        isActive: data.isActive,
        isVisible: data.isVisible,
        status: data.status,
        customCssClass: data.customCssClass,
        customId: data.customId,
        countryId: selectedTemplate.countryId,
        content: useDefaultContent ? {} : null, // null means backend will use default
        useDefaultContent,
      };

      console.log("payload : ", payload)

      await Axios.post(`page/${pageId}/sections`, payload);

      Block.remove('#add-section-dialog');
      setAddSectionDialog(false);
      await fetchPageData();

      Notify.success('Section added successfully', {
        timeout: 3000,
        position: 'center-top',
        cssAnimationStyle: 'from-top',
      });
    } catch (error) {
      console.error('Error adding section:', error);
      Block.remove('#add-section-dialog');
      Notify.failure('Error adding section', {
        timeout: 3000,
        position: 'center-top',
      });
    }
  };

  const handleEditSection = section => {
    setSelectedSection(section);

    // Check if section has custom content (not using default)
    const hasCustomContent =
      section.content && Object.keys(section.content).length > 0;

    reset({
      name: section.customName || section.template?.name || '',
      isActive: section.isActive === true,
      isVisible: section.isVisible === true,
      status: section.status || 'draft',
      customCssClass: section.customCssClass || '',
      customId: section.customId || '',
    });

    setEditSectionDialog(true);
  };

  const onSubmitEditSection = async data => {
    try {
      Block.circle('#edit-section-dialog', 'Updating section...');

      const payload = {
        isActive: data.isActive,
        isVisible: data.isVisible,
        status: data.status,
        customCssClass: data.customCssClass,
        customId: data.customId,
      };

      await Axios.put(`sections/${selectedSection.id}`, payload);

      Block.remove('#edit-section-dialog');
      setEditSectionDialog(false);
      await fetchPageData();

      Notify.success('Section updated successfully', {
        timeout: 3000,
        position: 'center-top',
      });
    } catch (error) {
      console.error('Error updating section:', error);
      Block.remove('#edit-section-dialog');
      Notify.failure('Error updating section', {
        timeout: 3000,
        position: 'center-top',
      });
    }
  };

  const handleDeleteSection = section => {
    if (section.status === 'published') {
      setConfirmModal({
        open: true,
        section: section,
        action: 'delete',
        pendingAction: () => {
          Confirm.show(
            '⚠️ Final Confirmation - Permanent Deletion',
            `You are about to permanently delete a LIVE section. This action CANNOT be undone. Are you absolutely sure?`,
            'Yes, Delete Permanently',
            'Cancel',
            async () => {
              try {
                Block.circle('#page-sections', 'Deleting section...');
                await Axios.delete(
                  `sections/${section.id}?permanent=true&label=${encodeURIComponent(section.template?.name || '')}`,
                );
                await fetchPageData();

                Notify.success('Section deleted successfully', {
                  timeout: 3000,
                  position: 'center-top',
                  cssAnimationStyle: 'from-top',
                });
              } catch (error) {
                console.error('Error deleting section:', error);
                Notify.failure('Error deleting section', {
                  timeout: 3000,
                  position: 'center-top',
                });
              } finally {
                Block.remove('#page-sections');
                setConfirmModal({
                  open: false,
                  section: null,
                  action: null,
                  pendingAction: null,
                });
              }
            },
            () => {
              setConfirmModal({
                open: false,
                section: null,
                action: null,
                pendingAction: null,
              });
            },
            {
              titleColor: '#f44336',
              okButtonBackground: '#f44336',
              borderRadius: '8px',
              cssAnimationStyle: 'from-top',
            },
          );
        },
      });
    } else {
      Confirm.show(
        'Delete Section',
        `Are you sure you want to delete "${section.template?.name}" from this page?`,
        'Yes, Delete',
        'Cancel',
        async () => {
          try {
            Block.circle('#page-sections', 'Deleting section...');
            await Axios.delete(
              `sections/${section.id}?permanent=true&label=${encodeURIComponent(section.template?.name || '')}`,
            );
            await fetchPageData();

            Notify.success('Section deleted successfully', {
              timeout: 3000,
              position: 'center-top',
            });
          } catch (error) {
            console.error('Error deleting section:', error);
            Notify.failure('Error deleting section', {
              timeout: 3000,
              position: 'center-top',
            });
          } finally {
            Block.remove('#page-sections');
          }
        },
        null,
        {
          titleColor: '#f44336',
          okButtonBackground: '#f44336',
          borderRadius: '8px',
        },
      );
    }
  };

  const updateSectionStatus = async (sectionId, newStatus) => {
    const section = sections.find(s => s.id === sectionId);

    if (section?.status === 'published' && newStatus !== 'published') {
      setConfirmModal({
        open: true,
        section: section,
        action: newStatus,
        pendingAction: async () => {
          try {
            Block.circle('#page-sections', 'Updating section status...');
            await Axios.put(`sections/${sectionId}`, {
              status: newStatus,
            });
            await fetchPageData();

            Notify.success(`Section ${newStatus} successfully`, {
              timeout: 3000,
              position: 'center-top',
            });
          } catch (error) {
            console.error('Error updating status:', error);
            Notify.failure('Error updating status', {
              timeout: 3000,
              position: 'center-top',
            });
          } finally {
            Block.remove('#page-sections');
            setConfirmModal({
              open: false,
              section: null,
              action: null,
              pendingAction: null,
            });
          }
        },
      });
    } else {
      try {
        Block.circle('#page-sections', 'Updating section status...');
        await Axios.put(`sections/${sectionId}`, {
          status: newStatus,
        });
        await fetchPageData();

        Notify.success(`Section ${newStatus} successfully`, {
          timeout: 3000,
          position: 'center-top',
        });
      } catch (error) {
        console.error('Error updating status:', error);
        Notify.failure('Error updating status', {
          timeout: 3000,
          position: 'center-top',
        });
      } finally {
        Block.remove('#page-sections');
      }
    }
  };

  const toggleVisibility = async section => {
    try {
      Block.circle('#page-sections', 'Updating visibility...');
      await Axios.put(`sections/${section.id}`, {
        isVisible: !section.isVisible,
      });
      await fetchPageData();

      Notify.success(
        `Section ${!section.isVisible ? 'visible' : 'hidden'} successfully`,
        {
          timeout: 2000,
          position: 'center-top',
        },
      );
    } catch (error) {
      console.error('Error toggling visibility:', error);
      Notify.failure('Error updating visibility', {
        timeout: 3000,
        position: 'center-top',
      });
    } finally {
      Block.remove('#page-sections');
    }
  };

  const editContent = section => {
    let isUsingDefault;

    if (section.useDefaultContent !== undefined) {
      isUsingDefault = section.useDefaultContent;
    } else {
      const keys = section.content ? Object.keys(section.content) : [];
      const isCharMap = keys.length > 0 && keys.every(k => !isNaN(k));

      if (isCharMap) {
        isUsingDefault = false;
      } else {
        isUsingDefault = keys.length === 0;
      }
    }

    navigate(`/sections/${section.id}`, {
      state: {
        pageId: pageId,
        useDefaultContent: isUsingDefault,
      },
    });
  };

  const getStatusColor = status => {
    const option = STATUS_OPTIONS.find(opt => opt.value === status);
    return option?.color || 'default';
  };

  const CountryChip = ({countryId, size = 'small'}) => {
    const country = getCountryDetails(countryId);
    if (!country) return null;

    return (
      <Tooltip title={`Country: ${country.countryName}`}>
        <Chip
          label={country.countryCode}
          size={size}
          variant="outlined"
          color="info"
          sx={{fontWeight: 'bold'}}
        />
      </Tooltip>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3} id="page-sections">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <MDTypography variant="h6" color="white">
                      Page Sections Builder
                    </MDTypography>
                    {page && (
                      <Box display="flex" alignItems="center" gap={2}>
                        <MDTypography
                          variant="body2"
                          color="white"
                          opacity={0.8}>
                          {page.name} ({page.route})
                        </MDTypography>
                        {page.country && (
                          <Chip
                            label={page.country.countryName}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.2)',
                              color: 'white',
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4} sx={{textAlign: 'right'}}>
                    <MDButton
                      variant="gradient"
                      color="dark"
                      onClick={() => navigate(`/pages`)}>
                      Back to Pages
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox p={3}>
                <MDTypography variant="h6" gutterBottom>
                  Available Sections
                  <Chip
                    label={`${filteredTemplates.length} templates available`}
                    size="small"
                    color="info"
                    sx={{ml: 2}}
                  />
                </MDTypography>

                {filteredTemplates.length === 0 ? (
                  <Paper
                    sx={{p: 4, textAlign: 'center', bgcolor: 'grey.50', mb: 4}}>
                    <MDTypography color="text">
                      {availableTemplates.length === 0
                        ? 'No templates available.'
                        : 'All available templates have been added to this page.'}
                    </MDTypography>
                  </Paper>
                ) : (
                  <Grid container spacing={2} sx={{mb: 4}}>
                    {filteredTemplates.map(template => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
                        <Card
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            '&:hover': {boxShadow: 6, bgcolor: 'grey.50'},
                            border: '1px solid',
                            borderColor: 'success.light',
                            position: 'relative',
                          }}
                          onClick={() => handleAddSection(template)}>
                          <Box sx={{position: 'absolute', top: 8, right: 8}}>
                            <CountryChip
                              countryId={template.countryId}
                              size="small"
                            />
                          </Box>

                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar
                              src={template.thumbnailUrl}
                              sx={{
                                bgcolor: 'primary.main',
                                width: 48,
                                height: 48,
                              }}>
                              {template.name.charAt(0)}
                            </Avatar>
                            <Box sx={{pr: 5, pl: 2}}>
                              <MDTypography variant="body2" fontWeight="bold">
                                {template.name}
                              </MDTypography>
                              <MDTypography variant="caption" color="text">
                                {template.category}
                              </MDTypography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}

                <Divider sx={{my: 3}} />

                <MDTypography variant="h6" gutterBottom>
                  Page Sections Order ({sections.length})
                </MDTypography>

                {sections.length === 0 ? (
                  <Paper sx={{p: 4, textAlign: 'center', bgcolor: 'grey.50'}}>
                    <MDTypography color="text">
                      No sections added yet. Click on any template above to add.
                    </MDTypography>
                  </Paper>
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="sections">
                      {provided => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}>
                          {sections.map((section, index) => (
                            <Draggable
                              key={section.id}
                              draggableId={section.id}
                              index={index}>
                              {(provided, snapshot) => (
                                <Paper
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  sx={{
                                    mb: 2,
                                    p: 2,
                                    bgcolor: snapshot.isDragging
                                      ? 'grey.100'
                                      : 'white',
                                    border: section.isActive
                                      ? '1px solid'
                                      : '1px dashed',
                                    borderColor: section.isActive
                                      ? 'primary.main'
                                      : 'grey.400',
                                    opacity: section.isVisible ? 1 : 0.7,
                                    position: 'relative',
                                    '&:hover': {boxShadow: 3},
                                  }}>
                                  <Box
                                    {...provided.dragHandleProps}
                                    sx={{
                                      position: 'absolute',
                                      left: 8,
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      cursor: 'grab',
                                      color: 'text.secondary',
                                    }}>
                                    <DragHandle />
                                  </Box>

                                  <Grid
                                    container
                                    spacing={2}
                                    alignItems="center">
                                    <Grid item xs={1}>
                                      <Chip
                                        label={index + 1}
                                        color="primary"
                                        size="small"
                                      />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={2}
                                        ml={4}>
                                        <Avatar
                                          src={section.template?.thumbnailUrl}
                                          sx={{width: 40, height: 40}}>
                                          {section.template?.name?.charAt(0)}
                                        </Avatar>
                                        <Box>
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            flexWrap="wrap">
                                            <MDTypography
                                              variant="body2"
                                              fontWeight="bold">
                                              {section.template?.name}
                                            </MDTypography>
                                            <CountryChip
                                              countryId={section.countryId}
                                              size="small"
                                            />
                                          </Box>
                                          {/* Show indicator if using default content */}
                                          {(() => {
                                            const keys = section.content
                                              ? Object.keys(section.content)
                                              : [];
                                            const isCharMap =
                                              keys.length > 0 &&
                                              keys.every(k => !isNaN(k));
                                            const isDefault = keys.length === 0;

                                            const showingDefault =
                                              section.useDefaultContent ??
                                              isDefault;

                                            return (
                                              <Chip
                                                size="small"
                                                label={
                                                  showingDefault
                                                    ? '📋 Default'
                                                    : '✏️ Custom'
                                                }
                                                color={
                                                  showingDefault
                                                    ? 'info'
                                                    : 'success'
                                                }
                                                variant="outlined"
                                                sx={{
                                                  mt: 0.5,
                                                  fontSize: '0.6rem',
                                                }}
                                              />
                                            );
                                          })()}
                                        </Box>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                      <Box
                                        display="flex"
                                        gap={1}
                                        flexWrap="wrap">
                                        <Chip
                                          label={section.status || 'draft'}
                                          color={getStatusColor(section.status)}
                                          size="small"
                                        />
                                        {section.status === 'published' && (
                                          <Tooltip title="⚠️ This section is LIVE on the website">
                                            <Chip
                                              label="LIVE"
                                              color="success"
                                              size="small"
                                              icon={<Public />}
                                              sx={{
                                                fontWeight: 'bold',
                                                bgcolor: '#4caf50',
                                                color: 'white',
                                                animation: 'pulse 2s infinite',
                                                '@keyframes pulse': {
                                                  '0%': {opacity: 1},
                                                  '50%': {opacity: 0.7},
                                                  '100%': {opacity: 1},
                                                },
                                              }}
                                            />
                                          </Tooltip>
                                        )}
                                        {!section.isActive && (
                                          <Chip
                                            label="Inactive"
                                            color="default"
                                            size="small"
                                          />
                                        )}
                                        {!section.isVisible && (
                                          <Chip
                                            label="Hidden"
                                            color="warning"
                                            size="small"
                                          />
                                        )}
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} md={2}>
                                      <Box display="flex" gap={0.5}>
                                        <Tooltip title="Set as Draft">
                                          <IconButton
                                            size="small"
                                            color={
                                              section.status === 'draft'
                                                ? 'primary'
                                                : 'default'
                                            }
                                            onClick={() =>
                                              updateSectionStatus(
                                                section.id,
                                                'draft',
                                              )
                                            }>
                                            <Unpublished fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Publish">
                                          <IconButton
                                            size="small"
                                            color={
                                              section.status === 'published'
                                                ? 'success'
                                                : 'default'
                                            }
                                            onClick={() =>
                                              updateSectionStatus(
                                                section.id,
                                                'published',
                                              )
                                            }>
                                            <Publish fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Archive">
                                          <IconButton
                                            size="small"
                                            color={
                                              section.status === 'archived'
                                                ? 'error'
                                                : 'default'
                                            }
                                            onClick={() =>
                                              updateSectionStatus(
                                                section.id,
                                                'archived',
                                              )
                                            }>
                                            <Archive fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                      <Box
                                        display="flex"
                                        justifyContent="flex-end"
                                        gap={1}>
                                        <Tooltip title="Edit Content">
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() =>
                                              editContent(section)
                                            }>
                                            <Edit fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Section Settings">
                                          <IconButton
                                            size="small"
                                            color="info"
                                            onClick={() =>
                                              handleEditSection(section)
                                            }>
                                            <Settings fontSize="small" />
                                          </IconButton>
                                        </Tooltip>

                                        <Tooltip
                                          title={
                                            section.isVisible ? 'Hide' : 'Show'
                                          }>
                                          <IconButton
                                            size="small"
                                            color={
                                              section.isVisible
                                                ? 'success'
                                                : 'default'
                                            }
                                            onClick={() =>
                                              toggleVisibility(section)
                                            }>
                                            {section.isVisible ? (
                                              <Visibility />
                                            ) : (
                                              <VisibilityOff />
                                            )}
                                          </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Delete">
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                              handleDeleteSection(section)
                                            }>
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Paper>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Section Dialog - Simplified with only toggle */}
      <Dialog
        open={addSectionDialog}
        onClose={() => setAddSectionDialog(false)}
        maxWidth="sm"
        fullWidth
        id="add-section-dialog">
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            Add Section: {selectedTemplate?.name}
            {selectedTemplate?.slot && (
              <Chip
                label={selectedTemplate.slot.displayName}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
            {selectedTemplate && (
              <CountryChip
                countryId={selectedTemplate.countryId}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmitAddSection)}>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info">
                  <Box>
                    <strong>Template:</strong> {selectedTemplate?.name} •{' '}
                    <strong>Category:</strong> {selectedTemplate?.category}
                    {selectedTemplate?.slot && (
                      <>
                        {' '}
                        • <strong>Slot:</strong>{' '}
                        {selectedTemplate.slot.displayName}
                      </>
                    )}
                    {selectedTemplate?.country && (
                      <>
                        {' '}
                        • <strong>Country:</strong>{' '}
                        {selectedTemplate.country.countryName}
                      </>
                    )}
                  </Box>
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Custom Name (Optional)"
                  {...register('name')}
                  placeholder="Leave empty to use template name"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Controller
                    name="status"
                    control={control}
                    defaultValue="draft"
                    render={({field}) => (
                      <Select label="Status" {...field}>
                        {STATUS_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Controller
                      name="isActive"
                      control={control}
                      defaultValue={true}
                      render={({field: {onChange, value}}) => (
                        <Switch
                          checked={value}
                          onChange={e => onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="Active"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Controller
                      name="isVisible"
                      control={control}
                      defaultValue={true}
                      render={({field: {onChange, value}}) => (
                        <Switch
                          checked={value}
                          onChange={e => onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="Visible on page"
                />
              </Grid>

              {/* ONLY TOGGLE - No content fields */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useDefaultContent}
                      onChange={e => setUseDefaultContent(e.target.checked)}
                      color="primary"
                      size="medium"
                    />
                  }
                  label={
                    <Box>
                      <MDTypography variant="body2" fontWeight="bold">
                        Use Default Content
                      </MDTypography>
                      <MDTypography variant="caption" color="textSecondary">
                        {useDefaultContent
                          ? 'Section will use template default content. Content editing will be disabled.'
                          : 'You can add custom content later by clicking Edit button after section is created.'}
                      </MDTypography>
                    </Box>
                  }
                  labelPlacement="end"
                  sx={{ml: 0}}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider />
                <MDTypography
                  variant="caption"
                  color="textSecondary"
                  sx={{display: 'block', mt: 2}}>
                  {useDefaultContent
                    ? 'Section will use template default content. No custom data will be saved.'
                    : 'Section will be created without content. Use Edit button later to add custom content.'}
                </MDTypography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddSectionDialog(false)}>Cancel</Button>
            <MDButton type="submit" variant="contained" color="primary">
              Add Section
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Section Dialog - Only for settings, no content fields */}
      <Dialog
        open={editSectionDialog}
        onClose={() => setEditSectionDialog(false)}
        maxWidth="sm"
        fullWidth
        id="edit-section-dialog">
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            Section Settings: {selectedSection?.template?.name}
            {selectedSection && (
              <CountryChip countryId={selectedSection.countryId} size="small" />
            )}
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmitEditSection)}>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Custom Name"
                  {...register('name')}
                  placeholder="Enter custom name for this section"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Controller
                      name="isActive"
                      control={control}
                      render={({field: {onChange, value}}) => (
                        <Switch
                          checked={value}
                          onChange={e => onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="Active"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Controller
                      name="isVisible"
                      control={control}
                      render={({field: {onChange, value}}) => (
                        <Switch
                          checked={value}
                          onChange={e => onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="Visible on page"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Controller
                    name="status"
                    control={control}
                    render={({field}) => (
                      <Select label="Status" {...field}>
                        {STATUS_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditSectionDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Save Settings
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <PublishedSectionConfirmationModal
        open={confirmModal.open}
        onClose={() =>
          setConfirmModal({
            open: false,
            section: null,
            action: null,
            pendingAction: null,
          })
        }
        onConfirm={() => {
          if (confirmModal.pendingAction) {
            confirmModal.pendingAction();
          }
        }}
        section={confirmModal.section}
        action={confirmModal.action}
        loading={loading}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default PageSectionBuilder;
